import { AlertTriangle, Lock, Upload } from 'lucide-react';
import { Modal } from '../ui/Modal';
import OsScroll from '../ui/OsScroll';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { VAULT_TYPE_ORDER, VAULT_TYPE_META } from '../VaultItemModal';
import { IMPORT_ORDER, IMPORT_SOURCES } from './vaultImportExportHelpers';

/** The import / export modal markup. Plain function called during render, so the element tree is unchanged. */
export function renderVaultImportExportModal({
  t,
  sealed,
  mode,
  onClose,
  fileInputRef,
  masterRef,
  sourceKey,
  fileName,
  parsed,
  sealedPassword,
  setSealedPassword,
  unsealing,
  skipDupes,
  setSkipDupes,
  importing,
  progress,
  exportFormat,
  exportPassword,
  setExportPassword,
  exportPasswordConfirm,
  setExportPasswordConfirm,
  plainAck,
  setPlainAck,
  masterSecret,
  setMasterSecret,
  exporting,
  error,
  setError,
  changeSource,
  typeCounts,
  pickFile,
  onFileInputChange,
  runUnseal,
  runImport,
  exportPasswordMismatch,
  runExport,
  canExport,
  CSV_EXPORTS,
  changeExportFormat,
  onExportKeyDown,
}) {
  const isImport = mode === 'import';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isImport ? t('vaultIO.importTitle') : t('vaultIO.exportTitle')}
      subtitle={isImport ? t('vaultIO.importSubtitle') : t('vaultIO.exportSubtitle')}
      size="lg"
    >
      {isImport ? (
        <div className="space-y-4">
          <div>
            <label
              className="modal-logo-label"
              style={{ display: 'block', marginBottom: '0.375rem' }}
            >
              {t('vaultIO.sourceLabel')}
            </label>
            {/* `.modal-picker` is the wrapper that carries the border; it flattens
                the trigger inside it (index.css). Same pattern as
                VaultSecretLinkModal/ShareLinkModal — it is what makes the control
                match `.modal-input`'s frame instead of the picker's own. */}
            <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
              <CompactSelectPicker
                value={sourceKey}
                onChange={changeSource}
                options={IMPORT_ORDER.map((key) => ({
                  value: key,
                  label: t(IMPORT_SOURCES[key].labelKey),
                }))}
                disabled={importing}
                className="w-full"
                aria-label={t('vaultIO.sourceLabel')}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={pickFile}
              disabled={importing}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Upload size={14} /> {t('vaultIO.chooseFile')}
            </button>
            <span
              className="text-sm text-gray-500"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {fileName || t('vaultIO.noFile')}
            </span>
          </div>

          {sealed && (
            <div className="vault-io-panel">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Lock size={14} style={{ flexShrink: 0 }} />
                <span className="text-sm" style={{ fontWeight: 500 }}>
                  {t('vaultIO.sealedDetected', 'This file is an encrypted vault export.')}
                </span>
              </div>
              <p
                className="text-xs text-gray-500 dark:text-zinc-400"
                style={{ margin: '6px 0 8px' }}
              >
                {t('vaultIO.sealedPrompt', 'Enter the password it was exported with to unlock it.')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="password"
                  className="modal-input"
                  style={{ flex: 1, minWidth: 0 }}
                  placeholder={t('vaultIO.sealedPasswordPlaceholder', 'File password')}
                  value={sealedPassword}
                  disabled={unsealing}
                  autoComplete="off"
                  onChange={(e) => {
                    setSealedPassword(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runUnseal();
                  }}
                />
                <button
                  type="button"
                  onClick={runUnseal}
                  disabled={unsealing || sealedPassword === ''}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
                  style={{ flexShrink: 0 }}
                >
                  {unsealing
                    ? t('vaultIO.unlockingFile', 'Unlocking…')
                    : t('vaultIO.unlockFile', 'Unlock file')}
                </button>
              </div>
            </div>
          )}

          {parsed && (
            /* Class, not an inline border: an inline style cannot respond to
               `html.dark`, and `--border` is never actually defined anywhere in
               the app, so `var(--border, #e5e7eb)` always resolved to the
               near-white fallback — bright in dark mode. `.vault-io-panel`
               matches `.modal-picker`'s frame so this sits level with the
               dropdown above it. */
            <div className="vault-io-panel">
              {parsed.items.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                  {t('vaultIO.nothingToImport')}
                </p>
              ) : (
                <>
                  <div
                    className="text-sm"
                    style={{ fontWeight: 500, marginBottom: 6 }}
                  >
                    {t('vaultIO.previewHeading', { count: parsed.items.length })}
                  </div>
                  <OsScroll style={{ maxHeight: 180 }}>
                    <div>
                      {VAULT_TYPE_ORDER.filter((type) => typeCounts[type]).map((type) => {
                        const meta = VAULT_TYPE_META[type] || VAULT_TYPE_META.custom;
                        const RowIcon = meta.Icon;
                        return (
                          <div
                            key={type}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '4px 2px',
                              fontSize: 13,
                            }}
                          >
                            <RowIcon size={14} />
                            <span style={{ flex: 1, minWidth: 0 }}>{t(meta.labelKey)}</span>
                            <span style={{ color: '#9ca3af' }}>{typeCounts[type]}</span>
                          </div>
                        );
                      })}
                    </div>
                  </OsScroll>
                </>
              )}
              {parsed.skipped > 0 && (
                <p
                  className="text-xs"
                  style={{ color: '#d97706', marginTop: 6 }}
                >
                  {t('vaultIO.skippedRows', { count: parsed.skipped })}
                </p>
              )}
              {parsed.passkeysFound > 0 && (
                <p className="text-xs" style={{ color: '#6b7280', marginTop: 6 }}>
                  {t('vaultIO.foundPasskeys', { count: parsed.passkeysFound })}
                </p>
              )}
            </div>
          )}

          {parsed && parsed.items.length > 0 && (
            <label className="ext-checkbox-label">
              <input
                type="checkbox"
                className="checkbox-brand"
                checked={skipDupes}
                onChange={(e) => setSkipDupes(e.target.checked)}
                disabled={importing}
              />
              {t('vaultIO.skipDuplicates')}
            </label>
          )}

          {importing && progress.total > 0 && (
            <div className="text-sm text-gray-600 dark:text-zinc-300">
              {t('vaultIO.importingProgress', { done: progress.done, total: progress.total })}
            </div>
          )}

          {error && <div className="vault-modal-error">{error}</div>}

          <input
            ref={fileInputRef}
            type="file"
            accept={IMPORT_SOURCES[sourceKey].ext.map((e) => `.${e}`).join(',')}
            style={{ display: 'none' }}
            onChange={onFileInputChange}
          />

          <div className="modal-footer-row">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={!parsed || parsed.items.length === 0 || importing}
              onClick={runImport}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            >
              {importing
                ? t('vaultIO.importingButton')
                : t('vaultIO.importButton', { count: parsed ? parsed.items.length : 0 })}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label
              className="modal-logo-label"
              style={{ display: 'block', marginBottom: '0.375rem' }}
            >
              {t('vaultIO.formatLabel')}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label
                className="ext-checkbox-label"
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {/* checkbox-brand: the Params-table custom control (circular
                    radio variant) — same as DbExportModal's format pickers. */}
                <input
                  type="radio"
                  name="vault-export-format"
                  className="checkbox-brand"
                  checked={exportFormat === 'encrypted'}
                  disabled={exporting}
                  onChange={() => changeExportFormat('encrypted')}
                />
                <span>{t('vaultIO.formatEncrypted', 'Encrypted (recommended)')}</span>
              </label>
              <label
                className="ext-checkbox-label"
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <input
                  type="radio"
                  name="vault-export-format"
                  className="checkbox-brand"
                  checked={exportFormat === 'plaintext'}
                  disabled={exporting}
                  onChange={() => changeExportFormat('plaintext')}
                />
                <span>{t('vaultIO.formatPlaintext', 'Plaintext')}</span>
              </label>
              {Object.entries(CSV_EXPORTS).map(([key, fmt]) => (
                <label
                  key={key}
                  className="ext-checkbox-label"
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <input
                    type="radio"
                    name="vault-export-format"
                    className="checkbox-brand"
                    checked={exportFormat === key}
                    disabled={exporting}
                    onChange={() => changeExportFormat(key)}
                  />
                  <span>{t(fmt.labelKey, fmt.fallback)}</span>
                </label>
              ))}
            </div>
          </div>

          {exportFormat === 'encrypted' ? (
            <div>
              <label
                className="modal-logo-label"
                style={{ display: 'block', marginBottom: '0.375rem' }}
              >
                {t('vaultIO.exportPasswordLabel', 'File password')}
              </label>
              <input
                type="password"
                className="modal-input w-full"
                placeholder={t('vaultIO.exportPasswordPlaceholder', 'File password')}
                value={exportPassword}
                disabled={exporting}
                autoComplete="off"
                onChange={(e) => setExportPassword(e.target.value)}
                onKeyDown={onExportKeyDown}
              />
              <input
                type="password"
                className="modal-input w-full"
                style={{ marginTop: 8 }}
                placeholder={t('vaultIO.exportPasswordConfirmPlaceholder', 'Repeat file password')}
                value={exportPasswordConfirm}
                disabled={exporting}
                autoComplete="off"
                onChange={(e) => setExportPasswordConfirm(e.target.value)}
                onKeyDown={onExportKeyDown}
              />
              <p className="text-xs text-gray-500 dark:text-zinc-400" style={{ marginTop: 6 }}>
                {t(
                  'vaultIO.exportPasswordHint',
                  'At least 8 characters. It can differ from your master passphrase — importing this file will ask for it.'
                )}
              </p>
              {exportPasswordMismatch && (
                <p className="text-xs text-red-600 dark:text-red-400" style={{ marginTop: 4 }}>
                  {t('vaultIO.exportPasswordMismatch', 'The passwords do not match.')}
                </p>
              )}
            </div>
          ) : (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: 'rgba(217, 119, 6, 0.1)',
                border: '1px solid rgba(217, 119, 6, 0.35)',
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <AlertTriangle
                  size={18}
                  className="text-amber-600"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <span className="text-sm text-amber-600">{t('vaultIO.plaintextWarning')}</span>
              </div>
              <label
                className="ext-checkbox-label"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10 }}
              >
                <input
                  type="checkbox"
                  className="checkbox-brand"
                  style={{ marginTop: 2 }}
                  checked={plainAck}
                  disabled={exporting}
                  onChange={(e) => setPlainAck(e.target.checked)}
                />
                <span className="text-sm text-amber-600">
                  {t(
                    'vaultIO.plaintextAck',
                    'I understand — anyone with this file can read every credential.'
                  )}
                </span>
              </label>
            </div>
          )}

          <div>
            <label
              className="modal-logo-label"
              style={{ display: 'block', marginBottom: '0.375rem' }}
            >
              {t('vaultIO.reauthLabel', 'Confirm your master passphrase')}
            </label>
            <input
              ref={masterRef}
              type="password"
              className="modal-input w-full"
              placeholder={t('vaultIO.reauthPlaceholder', 'Master passphrase')}
              value={masterSecret}
              disabled={exporting}
              autoComplete="current-password"
              onChange={(e) => {
                setMasterSecret(e.target.value);
                setError(null);
              }}
              onKeyDown={onExportKeyDown}
            />
            <p className="text-xs text-gray-500 dark:text-zinc-400" style={{ marginTop: 6 }}>
              {t(
                'vaultIO.reauthWhy',
                'An export contains every secret in the vault, so every export asks again.'
              )}
            </p>
          </div>

          {error && <div className="vault-modal-error">{error}</div>}

          <div className="modal-footer-row">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={!canExport}
              onClick={runExport}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            >
              {exporting ? t('vaultIO.exportingButton') : t('vaultIO.exportButton')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
